// src/components/DirectPaymentUnlock.js
/* global BigInt */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { PublicKey, SystemProgram, Transaction, TransactionInstruction } from "@solana/web3.js";
import { db } from "../firebaseConfig";
import { getMint, TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from '@solana/spl-token';
import { resolveDonationToken } from '../utils/donationTokens';
import {
    collection,
    onSnapshot,
    query,
    where,
} from "firebase/firestore";

import { useServiceFeePolicy } from '../hooks/useServiceFeePolicy';
import { decimalToAtomic, addSolServiceFee } from '../utils/serviceFees';
import { tokenPaymentInstructions } from '../utils/serviceFeeTransfers';
import ServiceFeeNotice from './ServiceFeeNotice';
import { usePaymentReceipt } from '../hooks/usePaymentReceipt';
import { isVerifiedPayment } from '../utils/activityEvents';
import PaymentReceiptRecovery from './PaymentReceiptRecovery';
const MEMO_PROGRAM_ID = new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr');

export default function DirectPaymentUnlock({ gate, componentId, componentType, ownerPublicKey, onUnlocked }) {
    const wallet = useWallet();
    const { publicKey, connected, sendTransaction } = wallet;
    const { connection } = useConnection();
    const { policy: fees, loading: feesLoading, error: feesError } = useServiceFeePolicy();
    const walletAddress = useRef('');
    walletAddress.current = publicKey?.toBase58?.() || '';
    const [paymentQuote, setPaymentQuote] = useState(null);

    const [paying, setPaying] = useState(false);
    const [error, setError] = useState("");
    const [myPayments, setMyPayments] = useState([]);
    const [mySubtotal, setMySubtotal] = useState(0);

    const priceAmount = Number(gate?.priceSol || 0);
    const payToUid = String(gate?.payToUid || "");
    const targetId = String(gate?.targetId || "");
    const ownerPublicKeyStr = String(ownerPublicKey || "");
    const payTo = payToUid || ownerPublicKeyStr;
    const receipt = usePaymentReceipt({ wallet, connection, kind: 'directPayment',
        spaceOwnerUid: ownerPublicKeyStr, componentId: String(componentId || targetId) });
    const currency = gate?.currency || 'SOL';
    const tokenConfig = resolveDonationToken(currency);
    const tokenMint = gate?.tokenMint || tokenConfig.mint || '';

    const recipient = useMemo(() => {
        try {
            return payTo ? new PublicKey(payTo) : null;
        } catch {
            return null;
        }
    }, [payTo]);

    const configError = !recipient ? "Direct payment not configured: invalid recipient address." :
        !(priceAmount > 0) ? "Direct payment not configured: missing price." :
        !targetId ? "Direct payment not configured: missing targetId." : feesError;

    const findSenderTokenAccount = async (mintPubkey) => {
        const programs = [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID];
        for (const programId of programs) {
            const accounts = await connection.getParsedTokenAccountsByOwner(
                publicKey,
                { programId },
                "confirmed"
            );
            const filtered = accounts.value.filter(
                (acc) => acc.account?.data?.parsed?.info?.mint === mintPubkey.toBase58()
            );
            if (!filtered.length) continue;
            const best = filtered
                .map((acc) => ({
                    pubkey: acc.pubkey,
                    amount: Number(acc.account?.data?.parsed?.info?.tokenAmount?.uiAmount || 0),
                    programId,
                }))
                .sort((a, b) => b.amount - a.amount)[0];
            if (best?.pubkey) return best;
        }
        return null;
    };

    const preparePaymentTransaction = async (tx, intent) => {
        tx.add(new TransactionInstruction({ programId: MEMO_PROGRAM_ID, keys: [], data: Buffer.from(intent.memo, 'utf8') }));
        if (gate?.memo) tx.add(new TransactionInstruction({ programId: MEMO_PROGRAM_ID, keys: [], data: Buffer.from(String(gate.memo), 'utf8') }));
        tx.feePayer = publicKey;
        const latestBlockhash = await connection.getLatestBlockhash("confirmed");
        tx.recentBlockhash = latestBlockhash.blockhash;
        return { tx, latestBlockhash };
    };

    const confirmPaymentTransaction = async (signature, latestBlockhash) => {
        const confirmation = await connection.confirmTransaction(
            {
                signature,
                blockhash: latestBlockhash.blockhash,
                lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
            },
            "confirmed"
        );
        if (confirmation?.value?.err) {
            throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
        }
    };

    useEffect(() => {
        if (!connected || !publicKey || !targetId || !payTo) {
            setMyPayments([]);
            setMySubtotal(0);
            return;
        }
        const q = query(
            collection(db, "paymentReceipts"),
            where("kind", "==", "directPayment"),
            where("targetId", "==", targetId),
            where("payTo", "==", payTo),
            where("payer", "==", publicKey.toBase58())
        );
        const unsub = onSnapshot(q, (snap) => {
            const rows = [];
            let sum = 0;
            snap.forEach((doc) => {
                const d = doc.data();
                if (!isVerifiedPayment(d) || d.cluster !== receipt.cluster) return;
                const recCurrency = d?.currency || 'SOL';
                const recMint = d?.tokenMint || '';
                const currencyMatch = currency ? currency === recCurrency : true;
                const mintMatch = tokenMint ? tokenMint === recMint : true;
                if (!currencyMatch || !mintMatch) return;
                const amountVal = typeof d.amount === "number" ? d.amount : d.amountSol;
                if (d?.status === "confirmed" && typeof amountVal === "number") sum += amountVal;
                rows.push({
                    id: doc.id,
                    amount: amountVal ?? 0,
                    currency: recCurrency,
                    txSig: d.txSig ?? null,
                    createdAt: d.createdAt ?? null,
                    status: d.status ?? null,
                });
            });
            rows.sort((a, b) => {
                const ta = a.createdAt?.seconds ?? 0;
                const tb = b.createdAt?.seconds ?? 0;
                return tb - ta;
            });
            setMyPayments(rows);
            setMySubtotal(sum);
        });
        return () => unsub();
    }, [connected, publicKey, targetId, payTo, currency, tokenMint, receipt.cluster]);

    const hasPaid = priceAmount > 0 && mySubtotal >= priceAmount;

    const sigUrl = (sig) => (sig ? `https://solscan.io/tx/${sig}` : null);

    const handlePay = async () => {
        setError("");

        if (configError || feesLoading) {
            setError(configError || 'Wait for service fees to load.');
            return;
        }
        if (!connected || !publicKey) {
            setError("Please connect a Solana wallet to unlock.");
            return;
        }

        setPaying(true);
        try {


            const payerAddress = publicKey.toBase58();
            const ensureCurrent = () => { if (walletAddress.current !== payerAddress) throw new Error('Wallet changed. Please retry.'); };
            let sig;
            if (tokenConfig.isNative || currency === 'SOL') {
                const amountRaw = decimalToAtomic(priceAmount);
                const intent = await receipt.prepare({ amount: priceAmount, amountRaw, recipient: payTo, currency, tokenMint: '',
                    expectedVersion: fees?.version || 0, targetId });
                const quote = intent.quote;
                setPaymentQuote({ ...quote, decimals: 9 });
                const tx = new Transaction().add(SystemProgram.transfer({ fromPubkey: publicKey, toPubkey: recipient, lamports: BigInt(amountRaw) }));
                addSolServiceFee(tx, publicKey, quote);
                const prepared = await preparePaymentTransaction(tx, intent);
                ensureCurrent();
                sig = await sendTransaction(prepared.tx, connection, { preflightCommitment: "confirmed" });
                const submitted = receipt.remember(intent, sig);
                await confirmPaymentTransaction(sig, prepared.latestBlockhash);
                await receipt.verify(submitted);
            } else {
                if (!tokenMint) throw new Error("Token mint is missing for this payment.");
                const mintPubkey = new PublicKey(tokenMint);
                const senderAccount = await findSenderTokenAccount(mintPubkey);
                if (!senderAccount?.pubkey) throw new Error("Sender token account not found. Please ensure you have this token on this network.");
                const senderAta = senderAccount.pubkey;
                const tokenProgramId = senderAccount.programId || TOKEN_PROGRAM_ID;
                let mintInfo = tokenConfig.decimals != null ? { decimals: tokenConfig.decimals, tlvData: null } : await getMint(connection, mintPubkey, undefined, tokenProgramId);
                if (tokenProgramId === TOKEN_2022_PROGRAM_ID && !mintInfo?.tlvData) mintInfo = await getMint(connection, mintPubkey, undefined, tokenProgramId);
                const decimals = mintInfo.decimals ?? 0;
                const amountRaw = decimalToAtomic(priceAmount, decimals);
                const intent = await receipt.prepare({ amount: priceAmount, amountRaw, recipient: payTo, currency, tokenMint,
                    expectedVersion: fees?.version || 0, targetId });
                const quote = intent.quote;
                setPaymentQuote({ ...quote, decimals });
                const instructions = await tokenPaymentInstructions({ connection, payer: publicKey, recipient, mint: mintPubkey, senderAta, tokenProgramId, mintInfo, decimals, quote });
                const tx = new Transaction().add(...instructions);
                addSolServiceFee(tx, publicKey, quote);
                const prepared = await preparePaymentTransaction(tx, intent);
                ensureCurrent();
                sig = await sendTransaction(prepared.tx, connection, { preflightCommitment: "confirmed" });
                const submitted = receipt.remember(intent, sig);
                await confirmPaymentTransaction(sig, prepared.latestBlockhash);
                await receipt.verify(submitted);
            }

            if (typeof onUnlocked === "function") onUnlocked(sig);
        } catch (e) {
            console.error("Direct payment failed:", e);
            setError(e?.message || "Payment failed");
        } finally {
            setPaying(false);
        }
    };

    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 14, opacity: 0.9 }}>
                Unlock this panel for <b>{priceAmount}</b> {currency}.
                <ServiceFeeNotice kind="directPayment" currency={currency} amount={priceAmount} decimals={tokenConfig.decimals}
                    isNative={tokenConfig.isNative || currency === 'SOL'} quote={paymentQuote} />
            </div>

            {hasPaid ? (
                <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 13, opacity: 0.85 }}>
                        Unlocked. Your total paid: {mySubtotal.toFixed(3)} {currency}.
                    </div>
                    <details style={{ marginTop: 8 }}>
                        <summary style={{ cursor: "pointer" }}>View transactions</summary>
                        <ul style={{ listStyle: "none", padding: 0, marginTop: 8 }}>
                            {myPayments.map((p) => {
                                const ts = p.createdAt?.seconds ? new Date(p.createdAt.seconds * 1000) : null;
                                const when = ts ? ts.toLocaleString() : "pending...";
                                const href = sigUrl(p.txSig);
                                return (
                                    <li
                                        key={p.id}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "auto 1fr auto",
                                            gap: 8,
                                            alignItems: "center",
                                            padding: "6px 0",
                                            borderTop: "1px solid rgba(0,0,0,0.08)",
                                        }}
                                    >
                                        <span style={{ fontWeight: 600 }}>{Number(p.amount || 0).toFixed(3)} {currency}</span>
                                        <span style={{ opacity: 0.8 }}>{when}</span>
                                        {href ? (
                                            <a href={href} target="_blank" rel="noreferrer">
                                                View Tx
                                            </a>
                                        ) : (
                                            <span style={{ opacity: 0.6 }}>No tx</span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </details>
                </div>
            ) : (
                <button
                    className="muulti-button"
                    type="button"
                    disabled={paying || feesLoading || !!configError || !!receipt.pending}
                    onClick={handlePay}
                    style={{ marginTop: 10 }}
                >
                    {paying ? "Processing…" : `Pay ${priceAmount} ${currency} to unlock`}
                </button>
            )}

            <PaymentReceiptRecovery receipt={receipt} onVerified={(result) => { setError(''); onUnlocked?.(result.txSig); }} />
            {error && error !== receipt.error ? (
                <div style={{ marginTop: 8, fontSize: 13, color: "#b00020" }}>{error}</div>
            ) : null}

            {configError ? (
                <div style={{ marginTop: 8, fontSize: 13, color: "#b00020" }}>{configError}</div>
            ) : null}
        </div>
    );
}
